import { useId } from 'react';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useI18n } from '@/hooks/useI18n';
import { identityHealthSummary } from '@/lib/identityHealth';
import type { IdentityHealthDetails } from '@/types/api';

interface IdentityHealthPopoverProps {
  name: string;
  score: number | null;
  details?: IdentityHealthDetails;
  children: ReactNode;
}

export function IdentityHealthPopover({
  name,
  score,
  details,
  children,
}: IdentityHealthPopoverProps) {
  const { t } = useI18n();
  const headingId = useId();
  const scoreLabel = score === null ? t('Non évaluable', 'Not enough data') : `${score}/100`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t(
            `Santé de ${name} : ${scoreLabel}. Voir les détails`,
            `Health of ${name}: ${scoreLabel}. View details`,
          )}
          className="inline-flex min-h-11 w-full items-center rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet lg:min-h-0 lg:justify-end lg:text-right"
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        collisionPadding={12}
        aria-labelledby={headingId}
        className="max-w-[calc(100vw-1.5rem)]"
      >
        <div className="mb-2 flex items-center justify-between gap-3">
          <h4 id={headingId} className="text-[13px] font-semibold">
            {t('Détails de santé', 'Health details')}
          </h4>
          <PopoverClose asChild>
            <button
              type="button"
              aria-label={t('Fermer les détails', 'Close details')}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-text-3 hover:bg-bg-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </PopoverClose>
        </div>
        <p className="text-[12px] leading-5 text-text-2">
          {identityHealthSummary(details, t) ??
            t(
              'Détails indisponibles pour cette identité.',
              'No health details available for this identity.',
            )}
        </p>
      </PopoverContent>
    </Popover>
  );
}
